<template>
  <div class="page policy-page">
    <h1>{{ t('policyTitle') }}</h1>
    <div class="hint">
      <span>{{ encryptReminder }}</span>
      <a class="hint-link" href="/.well-known/pgp-key.asc">{{ t('downloadPgp') }}</a>
    </div>
    <div class="doc">
      <aside class="toc" ref="toc">
        <div class="toc-label">{{ t('tocLabel') }}</div>
        <nav class="toc-nav" ref="tocNav">
          <a
            v-for="item in tocItems"
            :key="item.id"
            :href="'#' + item.id"
            :class="{ on: activeId === item.id }"
            :data-id="item.id"
            @click.prevent="scrollTo(item.id)"
          >{{ item.label }}</a>
        </nav>
      </aside>
      <div class="doc-body">
        <section id="welcome">
          <h2>{{ t('tocWelcome') }}</h2>
          <p>{{ field('welcome') || welcome }}</p>
          <p>{{ field('statement') || statement }}</p>
        </section>
        <section id="process">
          <h2>{{ t('tocProcess') }}</h2>
          <process-steps :steps="processSteps" />
          <p v-if="field('process')">{{ field('process') }}</p>
        </section>
        <section id="channels">
          <h2>{{ t('tocChannels') }}</h2>
          <p>
            <router-link class="btn btn-primary" to="/submit">{{ t('webSubmit') }}</router-link>
          </p>
          <mail-channel />
        </section>
        <section v-if="policyLines('researchRules').length" id="rules" class="rules-block">
          <h2>{{ t('tocRules') }}</h2>
          <ul>
            <li v-for="(line, i) in policyLines('researchRules')" :key="i">{{ line }}</li>
          </ul>
        </section>
        <section v-if="field('responseTimeline') || field('disclosureTimeline') || field('commExpect')" id="timeline">
          <h2>{{ t('tocTimeline') }}</h2>
          <p v-if="field('responseTimeline')">{{ field('responseTimeline') }}</p>
          <p v-if="field('disclosureTimeline')">{{ field('disclosureTimeline') }}</p>
          <p v-if="field('commExpect')">{{ field('commExpect') }}</p>
        </section>
        <section v-if="field('preDisclosure') || field('timelineCaveat')" id="disclosure">
          <h2>{{ t('tocDisclosure') }}</h2>
          <p v-if="field('preDisclosure')">{{ field('preDisclosure') }}</p>
          <p v-if="field('timelineCaveat')" class="muted-note">{{ field('timelineCaveat') }}</p>
        </section>
        <section v-if="field('conflict')" id="conflict">
          <h2>{{ t('tocConflict') }}</h2>
          <p>{{ field('conflict') }}</p>
        </section>
        <section v-if="field('scope')" id="scope">
          <h2>{{ t('tocScope') }}</h2>
          <p>{{ field('scope') }}</p>
        </section>
        <section v-if="field('exclusions')" id="exclusions">
          <h2>{{ t('tocExclusions') }}</h2>
          <p>{{ field('exclusions') }}</p>
        </section>
        <section v-if="field('safeHarbor')" id="harbor">
          <h2>{{ t('tocHarbor') }}</h2>
          <p>{{ field('safeHarbor') }}</p>
        </section>
        <section id="contact">
          <h2>{{ t('tocContact') }}</h2>
          <p v-if="policy.securityContact || brand.securityContact">
            <a class="mail-addr" :href="'mailto:' + (policy.securityContact || brand.securityContact)">{{ policy.securityContact || brand.securityContact }}</a>
          </p>
          <p v-if="field('extraContact')">{{ field('extraContact') }}</p>
        </section>
        <section id="files">
          <h2>{{ t('tocFiles') }}</h2>
          <p class="file-actions">
            <a class="btn btn-outline" href="/security.txt">{{ t('downloadSecurity') }}</a>
            <a class="btn btn-primary" href="/.well-known/pgp-key.asc">{{ t('downloadPgp') }}</a>
          </p>
          <pgp-meta />
        </section>
      </div>
    </div>
  </div>
</template>
<script>
import { t } from '../i18n'
import { mapState, mapGetters } from 'vuex'
import ProcessSteps from '../components/ProcessSteps.vue'
import MailChannel from '../components/MailChannel.vue'
import PgpMeta from '../components/PgpMeta.vue'

export default {
  name: 'Policy',
  components: { ProcessSteps, MailChannel, PgpMeta },
  data() {
    return {
      activeId: 'welcome',
      locking: false,
      lockTimer: null
    }
  },
  computed: {
    ...mapState(['locale', 'brand', 'policy']),
    ...mapGetters(['welcome', 'statement', 'encryptReminder', 'hasPgpPublicKey', 'processSteps', 'policyText', 'policyLines']),
    tocItems() {
      const items = [
        { id: 'welcome', label: this.t('tocWelcome'), show: true },
        { id: 'process', label: this.t('tocProcess'), show: true },
        { id: 'channels', label: this.t('tocChannels'), show: true },
        { id: 'rules', label: this.t('tocRules'), show: this.policyLines('researchRules').length > 0 },
        { id: 'timeline', label: this.t('tocTimeline'), show: !!(this.field('responseTimeline') || this.field('disclosureTimeline') || this.field('commExpect')) },
        { id: 'disclosure', label: this.t('tocDisclosure'), show: !!(this.field('preDisclosure') || this.field('timelineCaveat')) },
        { id: 'conflict', label: this.t('tocConflict'), show: !!this.field('conflict') },
        { id: 'scope', label: this.t('tocScope'), show: !!this.field('scope') },
        { id: 'exclusions', label: this.t('tocExclusions'), show: !!this.field('exclusions') },
        { id: 'harbor', label: this.t('tocHarbor'), show: !!this.field('safeHarbor') },
        { id: 'contact', label: this.t('tocContact'), show: true },
        { id: 'files', label: this.t('tocFiles'), show: true }
      ]
      return items.filter((item) => item.show)
    }
  },
  watch: {
    locale() {
      this.$nextTick(() => this.syncStickyOffset())
    }
  },
  created() {
    this.$store.dispatch('loadPolicy').catch(() => {})
  },
  mounted() {
    this.onScroll = () => this.updateActive()
    this.onResize = () => this.syncStickyOffset()
    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.onResize, { passive: true })
    this.$nextTick(() => {
      this.syncStickyOffset()
      this.updateActive()
      const hash = (this.$route.hash || '').replace(/^#/, '')
      if (hash && this.tocItems.some((item) => item.id === hash)) {
        this.scrollTo(hash, false)
      }
    })
  },
  beforeDestroy() {
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll)
    }
    if (this.onResize) {
      window.removeEventListener('resize', this.onResize)
    }
    if (this.lockTimer) {
      clearTimeout(this.lockTimer)
    }
  },
  methods: {
    t(key) {
      return t(this.locale, key)
    },
    field(name) {
      return this.policyText(name)
    },
    stickyOffset() {
      const header = document.querySelector('.site-header')
      const headerH = header ? header.offsetHeight : 64
      const toc = this.$refs.toc
      const mobile = window.innerWidth <= 900
      const tocH = mobile && toc ? toc.offsetHeight : 0
      return headerH + tocH + 12
    },
    syncStickyOffset() {
      const header = document.querySelector('.site-header')
      const headerH = header ? header.offsetHeight : 64
      const page = this.$el
      if (!page || !page.style) {
        return
      }
      page.style.setProperty('--policy-header-h', headerH + 'px')
      page.style.setProperty('--policy-sticky-offset', this.stickyOffset() + 'px')
    },
    scrollTo(id, smooth) {
      const el = document.getElementById(id)
      if (!el) {
        return
      }
      this.syncStickyOffset()
      this.activeId = id
      this.locking = true
      if (this.lockTimer) {
        clearTimeout(this.lockTimer)
      }
      const top = el.getBoundingClientRect().top + window.pageYOffset - this.stickyOffset()
      window.scrollTo({
        top: Math.max(0, top),
        behavior: smooth === false ? 'auto' : 'smooth'
      })
      if (history.replaceState) {
        history.replaceState(null, '', '#' + id)
      }
      this.$nextTick(() => this.ensureActiveVisible())
      this.lockTimer = setTimeout(() => {
        this.locking = false
        this.updateActive()
      }, smooth === false ? 50 : 600)
    },
    ensureActiveVisible() {
      const nav = this.$refs.tocNav
      if (!nav) {
        return
      }
      const link = nav.querySelector('a.on')
      if (!link || typeof link.scrollIntoView !== 'function') {
        return
      }
      // Only nudge when the nav itself scrolls (desktop overflow / rare narrow cases).
      if (nav.scrollHeight <= nav.clientHeight + 1 && nav.scrollWidth <= nav.clientWidth + 1) {
        return
      }
      link.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' })
    },
    updateActive() {
      if (this.locking) {
        return
      }
      const ids = this.tocItems.map((item) => item.id)
      const offset = this.stickyOffset()
      let current = ids[0] || 'welcome'
      ids.forEach((id) => {
        const el = document.getElementById(id)
        if (el && el.getBoundingClientRect().top <= offset + 8) {
          current = id
        }
      })
      this.activeId = current
    }
  }
}
</script>
